import { useId, useState } from 'react';
import './PasswordInput.css';

interface Props {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label: string;
  readonly autoComplete: 'new-password' | 'current-password';
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly minLength?: number;
  readonly hint?: string;
  /**
   * When true, renders the live Cognito password-policy checklist below
   * the input (12 chars / upper / lower / digit / symbol). Only attach
   * to the primary password field — not Confirm Password.
   */
  readonly showCriteria?: boolean;
  /**
   * When provided, renders a single "Passwords match" criterion below
   * the input. Ticks green when this input's value is non-empty AND
   * equals `matchAgainst`. Attach to the Confirm Password field with
   * `matchAgainst={password}`.
   */
  readonly matchAgainst?: string;
}

interface Criterion {
  readonly id: string;
  readonly label: string;
  readonly test: (v: string) => boolean;
}

// Order matches the pool's password policy in
// `infra/.../identity-stack.ts` (passwordPolicy).
const CRITERIA: readonly Criterion[] = [
  { id: 'length', label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { id: 'upper', label: 'Uppercase letter (A–Z)', test: (v) => /[A-Z]/.test(v) },
  { id: 'lower', label: 'Lowercase letter (a–z)', test: (v) => /[a-z]/.test(v) },
  { id: 'digit', label: 'Digit (0–9)', test: (v) => /\d/.test(v) },
  { id: 'symbol', label: 'Symbol (e.g. !@#$%)', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function PasswordInput({
  value,
  onChange,
  label,
  autoComplete,
  disabled,
  required,
  minLength,
  hint,
  showCriteria,
  matchAgainst,
}: Props) {
  const id = useId();
  const [show, setShow] = useState(false);

  // Build the criteria list. Two independent inputs: `showCriteria` adds
  // the policy items; `matchAgainst` adds the match item. They're
  // mutually exclusive in practice (primary uses one, confirm uses the
  // other) but the code combines them so both can coexist if needed.
  const items: Array<{ id: string; label: string; met: boolean }> = [];
  if (showCriteria) {
    for (const c of CRITERIA) {
      items.push({ id: c.id, label: c.label, met: c.test(value) });
    }
  }
  if (matchAgainst !== undefined) {
    items.push({
      id: 'match',
      label: 'Passwords match',
      met: value.length > 0 && value === matchAgainst,
    });
  }
  const renderList = items.length > 0;

  return (
    <div className="om-password">
      <label htmlFor={id} className="om-password__label">
        {label}
      </label>
      <div className="om-password__row">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          className="om-password__input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          minLength={minLength}
          spellCheck={false}
        />
        <button
          type="button"
          className="om-password__toggle"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          disabled={disabled}
        >
          {show ? 'hide' : 'show'}
        </button>
      </div>
      {renderList ? (
        <ul
          className="om-password__criteria"
          aria-label="Password requirements"
          aria-live="polite"
        >
          {items.map((c) => (
            <li
              key={c.id}
              className="om-password__criterion"
              data-met={c.met}
              aria-label={`${c.label}: ${c.met ? 'met' : 'not met'}`}
            >
              <span className="om-password__criterion-icon" aria-hidden="true">
                {c.met ? '✓' : '✕'}
              </span>
              <span className="om-password__criterion-label">{c.label}</span>
            </li>
          ))}
        </ul>
      ) : hint ? (
        <p className="om-password__hint">{hint}</p>
      ) : null}
    </div>
  );
}
